import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AddTierRequest } from '@/services/adminPaymentService';
import { Loader2 } from 'lucide-react';

interface AddTierModalProps {
  planName: string;
  existingTiers: string[];
  onAdd: (tierData: AddTierRequest) => Promise<void>;
  onClose: () => void;
}

const TIER_OPTIONS = [
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'family', label: 'Family' },
];

const AddTierModal: React.FC<AddTierModalProps> = ({ planName, existingTiers, onAdd, onClose }) => {
  const availableTiers = TIER_OPTIONS.filter(t => !existingTiers.includes(t.value));

  const [tier, setTier] = useState(availableTiers[0]?.value || '');
  const [monthlyBase, setMonthlyBase] = useState('19.99');
  const [monthlyDiscounted, setMonthlyDiscounted] = useState('');
  const [annualBase, setAnnualBase] = useState('199.99');
  const [annualDiscounted, setAnnualDiscounted] = useState('');
  const [description, setDescription] = useState('');
  const [discountCause, setDiscountCause] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!tier) {
      setError('Please select a tier');
      return;
    }

    const monthlyBaseNum = parseFloat(monthlyBase);
    const annualBaseNum = parseFloat(annualBase);

    if (isNaN(monthlyBaseNum) || monthlyBaseNum <= 0) {
      setError('Please enter a valid monthly base price');
      return;
    }

    if (isNaN(annualBaseNum) || annualBaseNum <= 0) {
      setError('Please enter a valid annual base price');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAdd({
        subscription_tier: tier,
        monthly_base_price: monthlyBaseNum,
        monthly_discounted_price: monthlyDiscounted ? parseFloat(monthlyDiscounted) : undefined,
        annual_base_price: annualBaseNum,
        annual_discounted_price: annualDiscounted ? parseFloat(annualDiscounted) : undefined,
        description: description || undefined,
        discount_cause: discountCause || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail?.message || err.message || 'Failed to add tier');
    } finally {
      setLoading(false);
    }
  };

  if (availableTiers.length === 0) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tier to {planName}</DialogTitle>
            <DialogDescription>
              All available tiers have already been added to this plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Tier to {planName}</DialogTitle>
          <DialogDescription>
            Create a new subscription tier with monthly and annual pricing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="text-sm text-red-500 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tier">Subscription Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger id="tier">
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent>
                {availableTiers.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="monthlyBase">Monthly Base Price ($)</Label>
              <Input
                id="monthlyBase"
                type="number"
                step="0.01"
                value={monthlyBase}
                onChange={e => setMonthlyBase(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthlyDiscounted">Monthly Discounted ($)</Label>
              <Input
                id="monthlyDiscounted"
                type="number"
                step="0.01"
                value={monthlyDiscounted}
                onChange={e => setMonthlyDiscounted(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="annualBase">Annual Base Price ($)</Label>
              <Input
                id="annualBase"
                type="number"
                step="0.01"
                value={annualBase}
                onChange={e => setAnnualBase(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="annualDiscounted">Annual Discounted ($)</Label>
              <Input
                id="annualDiscounted"
                type="number"
                step="0.01"
                value={annualDiscounted}
                onChange={e => setAnnualDiscounted(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Tier-specific description"
              maxLength={127}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="discountCause">Discount Cause (Optional)</Label>
            <Input
              id="discountCause"
              value={discountCause}
              onChange={e => setDiscountCause(e.target.value)}
              placeholder="e.g., promotion, early_bird"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Add Tier'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTierModal;
